import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Check, X, PauseCircle, Pencil, Eye, Search, Eraser } from 'lucide-react';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIst } from '../../lib/time';
import { Button, Input, Select, Modal, Textarea, Tooltip, message } from '../../ui';
import { Pill } from '../../components/TwDataTable';
import { ApproveOrderModal } from './ApproveOrderModal';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Approval decisions that need a reason. Each maps to its dedicated endpoint.
type ApprovalAction = 'reject' | 'hold' | 'correction';
const ACTION_META: Record<ApprovalAction, { path: string; title: string; ok: string; done: string; danger?: boolean }> = {
  reject: { path: 'reject', title: 'Reject order', ok: 'Reject', done: 'Order rejected', danger: true },
  hold: { path: 'hold', title: 'Put order on hold', ok: 'Hold', done: 'Order put on hold' },
  correction: { path: 'request-correction', title: 'Request correction', ok: 'Send back', done: 'Sent back for correction' },
};

/**
 * Order Approval Queue — every PLACED order awaiting a decision. Redesigned in Tailwind (was an antd DataTable):
 * the five per-row actions now sit on ONE line, and the queue filters client-side (it's bounded to 200 open
 * orders) so the Channel / Sales person / Retailer pickers only offer what's actually in the queue.
 */
export function OrderApprovalQueuePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const [actionFor, setActionFor] = useState<{ id: string; action: ApprovalAction } | null>(null);
  const [approveFor, setApproveFor] = useState<{ id: string; orderNo?: string } | null>(null);
  const [reason, setReason] = useState('');

  // ---- filters (client-side over the loaded queue) ----
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [channel, setChannel] = useState('ALL');
  const [salesPersonId, setSalesPersonId] = useState('ALL');
  const [retailerId, setRetailerId] = useState('ALL');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['orders-pending-approval'],
    queryFn: () => request<any>({ url: '/orders', params: { status: 'PLACED', pageSize: 200 } }),
    placeholderData: keepPreviousData,
  });
  const allRows: any[] = data?.data ?? [];

  const { data: salesUsers } = useQuery({
    queryKey: ['sales-users'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
  });
  const { data: retailers } = useQuery({
    queryKey: ['retailers-min'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => new Map<string, any>((retailers?.data ?? []).map((r: any) => [String(r.id), r])), [retailers]);
  const salesUserMap = useMemo(() => new Map<string, string>((salesUsers?.data ?? []).map((u: any) => [String(u.id), u.fullName])), [salesUsers]);

  const retailerName = (o: any) => {
    const r = retailerMap.get(String(o.retailerId));
    return r ? `${r.companyName}${r.city ? ` · ${r.city}` : ''}` : (o.companyName || `#${o.retailerId}`);
  };

  // Filter options derived from the queue itself, so a picker only lists what's actually pending.
  const channelOpts = useMemo(() => {
    const s = new Set<string>();
    for (const o of allRows) if (o.channel) s.add(o.channel);
    return [{ value: 'ALL', label: 'All channels' }, ...Array.from(s).sort().map((c) => ({ value: c, label: c }))];
  }, [allRows]);
  const salesPersonOpts = useMemo(() => {
    const s = new Set<string>();
    for (const o of allRows) if (o.salesPersonId) s.add(String(o.salesPersonId));
    const opts = Array.from(s).map((id) => ({ value: id, label: salesUserMap.get(id) ?? `#${id}` })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'ALL', label: 'All sales persons' }, ...opts];
  }, [allRows, salesUserMap]);
  const retailerOpts = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of allRows) if (o.retailerId) m.set(String(o.retailerId), retailerName(o));
    const opts = Array.from(m, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'ALL', label: 'All retailers' }, ...opts];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, retailerMap]);

  const min = minTotal.trim() === '' ? null : Number(minTotal);
  const max = maxTotal.trim() === '' ? null : Number(maxTotal);
  const rows = useMemo(() => {
    let rs = allRows;
    const q = search.trim().toLowerCase();
    if (q) rs = rs.filter((o) => (o.orderNo ?? '').toLowerCase().includes(q) || retailerName(o).toLowerCase().includes(q));
    if (channel !== 'ALL') rs = rs.filter((o) => o.channel === channel);
    if (salesPersonId !== 'ALL') rs = rs.filter((o) => String(o.salesPersonId) === salesPersonId);
    if (retailerId !== 'ALL') rs = rs.filter((o) => String(o.retailerId) === retailerId);
    // A min > max entry is treated as a valid [max, min] band so it never hides everything.
    if (min != null) rs = rs.filter((o) => Number(o.grandTotal) >= (max != null ? Math.min(min, max) : min));
    if (max != null) rs = rs.filter((o) => Number(o.grandTotal) <= (min != null ? Math.max(min, max) : max));
    return rs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, search, channel, salesPersonId, retailerId, min, max, retailerMap]);

  const hasFilters = !!(searchInput || channel !== 'ALL' || salesPersonId !== 'ALL' || retailerId !== 'ALL' || minTotal || maxTotal);
  const clearFilters = () => {
    setSearchInput(''); setChannel('ALL'); setSalesPersonId('ALL'); setRetailerId('ALL'); setMinTotal(''); setMaxTotal('');
  };

  // Refresh the queue AND the order's detail views so the detail page shows the new status without a reload.
  const refreshOrder = (id: string) => {
    qc.invalidateQueries({ queryKey: ['orders-pending-approval'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['order', id] });
    qc.invalidateQueries({ queryKey: ['order-timeline', id] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const actionMut = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: ApprovalAction; reason: string }) =>
      request({ url: `/orders/${id}/${ACTION_META[action].path}`, method: 'POST', data: { reason } }),
    onSuccess: (_d, { id, action }) => {
      message.success(ACTION_META[action].done);
      setActionFor(null); setReason('');
      refreshOrder(id);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const stop = (e: any) => e.stopPropagation();
  const meta = actionFor ? ACTION_META[actionFor.action] : null;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h1 className="text-xl font-semibold text-content">Order Approval Queue</h1>
        <Pill color="blue">{allRows.length} pending</Pill>
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface px-3 py-2.5">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Order no / retailer…" size="sm" className="w-52 pl-8" />
        </div>
        <Select value={channel} onChange={setChannel} options={channelOpts} size="sm" className="w-40" />
        <Select value={salesPersonId} onChange={setSalesPersonId} options={salesPersonOpts} size="sm" className="w-48" />
        {canViewRetailers && <Select value={retailerId} onChange={setRetailerId} options={retailerOpts} size="sm" className="w-56" />}
        <div className="flex items-center gap-1 text-sm text-muted">
          <span className="text-xs">₹</span>
          <Input type="number" value={minTotal} onChange={(e) => setMinTotal(e.target.value)} placeholder="Min" size="sm" className="w-20" />
          <span className="text-subtle">–</span>
          <Input type="number" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} placeholder="Max" size="sm" className="w-20" />
        </div>
        {hasFilters && <Button variant="text" size="sm" icon={<Eraser size={15} />} onClick={clearFilters}>Clear</Button>}
        {hasFilters && <span className="ml-auto text-xs text-subtle">{rows.length} of {allRows.length}</span>}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-card border border-line bg-surface">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-2xs font-semibold uppercase tracking-wide text-muted">
              <th className="px-3 py-2.5">Order No</th>
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Retailer</th>
              <th className="px-3 py-2.5">Channel</th>
              <th className="px-3 py-2.5 text-right">Subtotal</th>
              <th className="px-3 py-2.5 text-right">Total</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted">Loading…</td></tr>}
            {!isLoading && !rows.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted">{allRows.length ? 'No orders match these filters.' : 'No orders awaiting approval 🎉'}</td></tr>}
            {rows.map((o) => (
              <tr key={o.id} className="cursor-pointer border-b border-line/70 hover:bg-surface-2" onClick={() => navigate(`/orders/${o.id}`)}>
                <td className="whitespace-nowrap px-3 py-2.5 font-medium text-brand-700 dark:text-brand-400">{o.orderNo}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-muted">{formatIst(o.orderDate)}</td>
                <td className="px-3 py-2.5 text-content">{retailerName(o)}</td>
                <td className="px-3 py-2.5"><Pill>{o.channel}</Pill></td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-muted">{inr(o.subtotal)}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-content">{inr(o.grandTotal)}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1.5" onClick={stop}>
                    <Button size="sm" variant="primary" icon={<Check size={14} />} onClick={() => setApproveFor({ id: o.id, orderNo: o.orderNo })}>Approve</Button>
                    <Button size="sm" variant="soft-danger" icon={<X size={14} />} onClick={() => setActionFor({ id: o.id, action: 'reject' })}>Reject</Button>
                    <Tooltip label="Put on hold (stock / credit pending)">
                      <Button size="sm" variant="default" icon={<PauseCircle size={14} />} onClick={() => setActionFor({ id: o.id, action: 'hold' })}>Hold</Button>
                    </Tooltip>
                    <Tooltip label="Send back to the placer for correction">
                      <Button size="sm" variant="default" icon={<Pencil size={14} />} onClick={() => setActionFor({ id: o.id, action: 'correction' })}>Fix</Button>
                    </Tooltip>
                    <Button size="sm" variant="text" icon={<Eye size={14} />} onClick={() => navigate(`/orders/${o.id}`)}>View</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reason modal — reject / hold / correction */}
      <Modal
        open={!!actionFor}
        onOpenChange={(open) => { if (!open) { setActionFor(null); setReason(''); } }}
        title={meta?.title ?? ''}
        width={460}
        footer={
          <>
            <Button variant="text" onClick={() => { setActionFor(null); setReason(''); }}>Cancel</Button>
            <Button
              variant={meta?.danger ? 'danger' : 'primary'}
              disabled={!reason.trim()}
              loading={actionMut.isPending}
              onClick={() => actionFor && actionMut.mutate({ id: actionFor.id, action: actionFor.action, reason: reason.trim() })}
            >
              {meta?.ok ?? 'OK'}
            </Button>
          </>
        }
      >
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
          placeholder="Reason (mandatory — visible in audit + status history)" />
      </Modal>

      <ApproveOrderModal
        open={!!approveFor}
        orderId={approveFor?.id ?? null}
        orderNo={approveFor?.orderNo}
        onClose={() => setApproveFor(null)}
      />
    </div>
  );
}
